import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { gorseliIndir } from './imageDownload';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const CIFT_DOKUNUS_ZOOM = 2.5;
const CIFT_DOKUNUS_SURESI = 300;
const SWIPE_ESIGI = 50;

const clamp = (deger, alt, ust) => Math.min(Math.max(deger, alt), ust);

/**
 * Tam ekran görsel önizleme: çift dokunma / parmak ayırma / butonlarla yakınlaştırma,
 * yakınlaştırıldığında kaydırarak gezinme. Kat planı gibi detaylı görseller için.
 *
 * images: [{ src, label }]
 */
const ImageViewer = ({ images, startIndex = 0, onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [indiriliyor, setIndiriliyor] = useState(false);
  const zoomRef = useRef(MIN_ZOOM);
  const scrollRef = useRef(null);

  const gorsel = images[index];
  const cokluGorsel = images.length > 1;

  const oncekiGorsel = useCallback(
    () => setIndex((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );
  const sonrakiGorsel = useCallback(
    () => setIndex((i) => (i + 1) % images.length),
    [images.length]
  );

  // Ekrandaki odak noktası sabit kalacak şekilde yakınlaştırır
  const zoomTo = useCallback((hedefZoom, odak) => {
    const el = scrollRef.current;
    const oncekiZoom = zoomRef.current;
    const yeniZoom = clamp(hedefZoom, MIN_ZOOM, MAX_ZOOM);
    if (!el || yeniZoom === oncekiZoom) return;

    const rect = el.getBoundingClientRect();
    const odakX = odak ? odak.x - rect.left : rect.width / 2;
    const odakY = odak ? odak.y - rect.top : rect.height / 2;
    const oran = yeniZoom / oncekiZoom;
    const solaKaydir = (el.scrollLeft + odakX) * oran - odakX;
    const yukariKaydir = (el.scrollTop + odakY) * oran - odakY;

    zoomRef.current = yeniZoom;
    setZoom(yeniZoom);
    // Yeni genişlik uygulandıktan sonra kaydırma konumunu düzelt
    requestAnimationFrame(() => {
      el.scrollLeft = solaKaydir;
      el.scrollTop = yukariKaydir;
    });
  }, []);

  const zoomSifirla = useCallback(() => {
    const el = scrollRef.current;
    zoomRef.current = MIN_ZOOM;
    setZoom(MIN_ZOOM);
    if (el) {
      el.scrollLeft = 0;
      el.scrollTop = 0;
    }
  }, []);

  // Görsel değişince yakınlaştırma başa döner
  useEffect(() => {
    zoomSifirla();
  }, [index, zoomSifirla]);

  // Arka plan kaydırmasını kilitle + klavye kısayolları
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (!cokluGorsel) return;
      if (event.key === 'ArrowRight') sonrakiGorsel();
      if (event.key === 'ArrowLeft') oncekiGorsel();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, cokluGorsel, oncekiGorsel, sonrakiGorsel]);

  // React'in touch/wheel olayları passive olduğu için preventDefault çalışmaz; native dinleyici gerekli
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let parmakMesafesi = 0;
    let parmakZoom = MIN_ZOOM;
    let sonDokunus = 0;
    let swipeBaslangici = null;

    const mesafe = (touches) => Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    );

    const onTouchStart = (event) => {
      if (event.touches.length === 1) {
        swipeBaslangici = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        return;
      }
      if (event.touches.length !== 2) return;
      event.preventDefault();
      swipeBaslangici = null;
      parmakMesafesi = mesafe(event.touches);
      parmakZoom = zoomRef.current;
    };

    const onTouchMove = (event) => {
      if (event.touches.length !== 2 || !parmakMesafesi) return;
      event.preventDefault();
      zoomTo(parmakZoom * (mesafe(event.touches) / parmakMesafesi), {
        x: (event.touches[0].clientX + event.touches[1].clientX) / 2,
        y: (event.touches[0].clientY + event.touches[1].clientY) / 2
      });
    };

    const onTouchEnd = (event) => {
      if (event.touches.length < 2) parmakMesafesi = 0;
      if (event.touches.length > 0 || event.changedTouches.length !== 1) return;

      const dokunus = event.changedTouches[0];

      // Yakınlaştırma yokken sağa/sola kaydırarak görsel değiştir
      if (swipeBaslangici && cokluGorsel && zoomRef.current === MIN_ZOOM) {
        const dx = dokunus.clientX - swipeBaslangici.x;
        const dy = dokunus.clientY - swipeBaslangici.y;
        swipeBaslangici = null;
        if (Math.abs(dx) >= SWIPE_ESIGI && Math.abs(dx) > Math.abs(dy)) {
          sonDokunus = 0;
          if (dx < 0) sonrakiGorsel();
          else oncekiGorsel();
          return;
        }
      }
      swipeBaslangici = null;

      // Çift dokunma: yakınlaştır / eski haline döndür
      if (event.timeStamp - sonDokunus < CIFT_DOKUNUS_SURESI) {
        event.preventDefault();
        sonDokunus = 0;
        if (zoomRef.current > MIN_ZOOM) zoomSifirla();
        else zoomTo(CIFT_DOKUNUS_ZOOM, { x: dokunus.clientX, y: dokunus.clientY });
      } else {
        sonDokunus = event.timeStamp;
      }
    };

    // Masaüstünde Ctrl/⌘ + tekerlek ile yakınlaştırma; düz tekerlek kaydırmaya kalsın
    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      zoomTo(zoomRef.current * (event.deltaY < 0 ? 1.15 : 1 / 1.15), {
        x: event.clientX,
        y: event.clientY
      });
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('wheel', onWheel);
    };
  }, [zoomTo, zoomSifirla, cokluGorsel, oncekiGorsel, sonrakiGorsel]);

  // Görselin dışındaki boşluğa dokununca kapat
  const onArkaPlanClick = (event) => {
    if (event.target === scrollRef.current && zoomRef.current === MIN_ZOOM) onClose();
  };

  const onIndir = async () => {
    setIndiriliyor(true);
    try {
      await gorseliIndir(gorsel.src, gorsel.dosyaAdi, gorsel.label);
    } finally {
      setIndiriliyor(false);
    }
  };

  const kontrolButonu = 'flex items-center justify-center w-11 h-11 rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/25 active:scale-95 disabled:opacity-30';

  // Sayfa içindeki yığılma bağlamlarına takılmaması için doğrudan body'ye basılır
  return createPortal(
    <div className="fixed inset-0 z-100 bg-black animate-fadeIn">
      <div
        ref={scrollRef}
        onClick={onArkaPlanClick}
        style={{ touchAction: 'pan-x pan-y' }}
        className={`absolute inset-0 overflow-auto overscroll-contain flex justify-start ${zoom === MIN_ZOOM ? 'items-center' : 'items-start'}`}
      >
        <img
          src={gorsel.src}
          alt={gorsel.label}
          draggable={false}
          style={{ width: `${zoom * 100}%`, maxWidth: 'none' }}
          className="shrink-0 h-auto select-none"
        />
      </div>

      {/* Üst bar — çentik / dinamik ada altında kalmasın */}
      <div
        style={{ paddingTop: 'calc(var(--safe-top) + 1rem)' }}
        className="absolute top-0 inset-x-0 flex items-start gap-3 px-4 pb-10 bg-linear-to-b from-black/80 to-transparent pointer-events-none"
      >
        <div className="flex-1 min-w-0 pointer-events-auto">
          <p className="text-white font-semibold text-sm leading-tight">{gorsel.label}</p>
          {cokluGorsel && (
            <p className="text-white/60 text-xs mt-0.5">{index + 1} / {images.length}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onIndir}
          disabled={indiriliyor}
          aria-label="Görseli indir"
          className={`${kontrolButonu} pointer-events-auto shrink-0`}
        >
          {indiriliyor ? (
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
              <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 19h16" />
            </svg>
          )}
        </button>

        <button
          type="button"
          onClick={onClose}
          aria-label="Kapat"
          className={`${kontrolButonu} pointer-events-auto shrink-0`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Alt kontroller — ana ekran çubuğunun üstünde kalsın */}
      <div
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
        className="absolute bottom-0 inset-x-0 px-4 pt-10 bg-linear-to-t from-black/80 to-transparent"
      >
        <div className="flex items-center justify-center gap-3">
          {cokluGorsel && (
            <button
              type="button"
              onClick={oncekiGorsel}
              aria-label="Önceki görsel"
              className={kontrolButonu}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          <button
            type="button"
            onClick={() => zoomTo(zoomRef.current / 1.5)}
            disabled={zoom <= MIN_ZOOM}
            aria-label="Uzaklaştır"
            className={kontrolButonu}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>

          <button
            type="button"
            onClick={zoomSifirla}
            className="min-w-16 h-11 px-3 rounded-full bg-white/15 text-white text-sm font-semibold backdrop-blur-sm transition-colors hover:bg-white/25 active:scale-95"
          >
            {Math.round(zoom * 100)}%
          </button>

          <button
            type="button"
            onClick={() => zoomTo(zoomRef.current * 1.5)}
            disabled={zoom >= MAX_ZOOM}
            aria-label="Yakınlaştır"
            className={kontrolButonu}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          {cokluGorsel && (
            <button
              type="button"
              onClick={sonrakiGorsel}
              aria-label="Sonraki görsel"
              className={kontrolButonu}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        <p className="mt-3 text-center text-white/60 text-[11px]">
          Yakınlaştırmak için çift dokunun{cokluGorsel && ', görsel değiştirmek için sağa/sola kaydırın'}
        </p>
      </div>
    </div>,
    document.body
  );
};

export default ImageViewer;
